import AcceptedIcon from "@mui/icons-material/Check";
import RejectedIcon from "@mui/icons-material/Close";
import OfferedIcon from "@mui/icons-material/QuestionMark";
import WarningIcon from "@mui/icons-material/WarningAmber";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { Stage } from "../../../shared/CaseConfiguration";
import { OfferStatus } from "../../../shared/CaseModel";
import { percentage } from "../../../shared/percentage";
import { settlesBeforeJudgment } from "../../../shared/settlementProspects";
import { AdjustedPairStage } from "../../../shared/settlementAdjusted";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { TonalBadge } from "../../general/TonalBadge";
import { offerLook } from "./offerStatus";
import { Marked } from "../../guide/GuideMarker";
import { PercentSlider } from "./PercentSlider";
import SettlementOfferCard, { Offer } from "./SettlementOfferCard";

/** The width of the middle of the timeline, which its line runs down, in px. */
const SPINE_WIDTH = 72;
/** How far below the top of its row the number of an offer sits, in px. */
const MARK_TOP = 46;
/** The sizes of the number of an offer and of the icon at its arrow, in px. */
const MARK_SIZE = 30;
const ICON_SIZE = 22;
/** A card is no wider than this, in px. */
const CARD_MAX_WIDTH = 486;
/** The timeline scrolls sideways in a window narrower than this, in px. */
const MIN_WIDTH = 900;

const COLUMNS = `minmax(0, 1fr) ${SPINE_WIDTH}px minmax(0, 1fr)`;
/** The columns of a stage's line: the same in every line, so the figures align. */
const STAGE_COLUMNS = "minmax(0, 1fr) 250px 112px 84px 128px";
/** How strongly the current stage is washed with the primary colour, and its edge. */
const CURRENT_ALPHA = 0.07;
const CURRENT_EDGE_ALPHA = 0.25;
/** How strongly a stage's line shades the offers under it. */
const SHADE_ALPHA = 0.18;
const EDGE = 2;

/** A stage on the timeline, with what is expected of a settlement at it. */
export type StageOfTimeline = {
  stage: Stage;
  status: "closed" | "current" | "coming";
  chance: number;
  // from the current stage on, while a settlement can still happen
  prospect?: AdjustedPairStage;
};

/**
 * What stands at the tip of the arrow of an offer that has been made. A draft
 * has not gone to the other side: it has no arrow, and nothing is said there.
 */
const ARROW_ICONS: { [status in OfferStatus]?: typeof AcceptedIcon } = {
  [OfferStatus.Offered]: OfferedIcon,
  [OfferStatus.Rejected]: RejectedIcon,
  [OfferStatus.Accepted]: AcceptedIcon,
};

/** A stage across the timeline: its chance of settling and what comes of it. */
const StageLine = ({
  line: { stage, status, chance, prospect },
  name,
  readOnly,
  outsideFor,
  onChange,
}: {
  line: StageOfTimeline;
  name: string;
  readOnly: boolean;
  // who would do better by litigating than by taking the offer on the table
  outsideFor: string;
  onChange: (chance: number) => void;
}) => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.prospects",
  });
  const closed = status === "closed";
  // why the chance of a stage to come cannot be set
  const why = closed
    ? undefined
    : !settlesBeforeJudgment(stage)
      ? t("afterJudgment")
      : prospect?.noRange && !prospect.outsideRange && prospect.amount === undefined
        ? t("noRangeHere")
        : undefined;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: STAGE_COLUMNS,
        columnGap: 1.75,
        alignItems: "center",
        minHeight: 46,
        px: 2,
        typography: "body2",
        color: closed ? "text.secondary" : "text.primary",
        ...TABULAR_NUMERALS,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}
      >
        {name}
        {status !== "coming" && (
          <TonalBadge>{t(status)}</TonalBadge>
        )}
      </Box>
      <Tooltip title={why ?? ""} describeChild>
        <Box sx={{ display: "flex", minWidth: 0 }}>
          <PercentSlider
            value={chance}
            label={t("chanceOf", { stage: name })}
            disabled={readOnly || !prospect || why !== undefined}
            onChange={onChange}
          />
        </Box>
      </Tooltip>
      {prospect ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
              color: prospect.outsideRange ? "warning.dark" : undefined,
            }}
          >
            {prospect.outsideRange && (
              <Tooltip title={t("outsideRange", { stage: name, name: outsideFor })}>
                <WarningIcon
                  aria-label={t("outsideRange", {
                    stage: name,
                    name: outsideFor,
                  })}
                  sx={{ fontSize: 16 }}
                />
              </Tooltip>
            )}
            {prospect.amount === undefined
              ? "--"
              : currency.format(prospect.amount)}
          </Box>
          <Box sx={{ textAlign: "right" }}>
            {percentage.format(prospect.here)}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              aria-hidden
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              <Box
                sx={{
                  height: 1,
                  width: `${prospect.after * 100}%`,
                  bgcolor: "primary.light",
                }}
              />
            </Box>
            <Box sx={{ flex: "none", width: 40, textAlign: "right" }}>
              {percentage.format(prospect.after)}
            </Box>
          </Box>
        </>
      ) : (
        <>
          <span />
          <span />
          <span />
        </>
      )}
    </Box>
  );
};

/**
 * The offers between a party and one opponent as a timeline: the stages as
 * lines across it, each with the chance of settling at it, and between them
 * the offers of the stage, the party's own to the left, its opponent's to the
 * right, in the order they were made.
 */
const SettlementTimeline = ({
  partyName,
  opponentName,
  offers = [],
  settlement,
  stages,
  rangePanel,
  resulting,
  onChanceChange,
  onStatusChange,
  onDeleteRequested,
  onEditRequested,
  onInfoRequested,
}: {
  partyName: string;
  opponentName: string;
  offers?: Offer[];
  /**
   * The settlement that closes the timeline: once the two parties have
   * settled, no further offer is made.
   */
  settlement?: { stage: Stage; amount: number };
  stages: StageOfTimeline[];
  // shown under the offers of the current stage
  rangePanel?: ReactNode;
  // what the chances of the stages to come add up to
  resulting?: { settling: number; litigation: number };
  // a timeline without a callback leaves the matching action out
  onChanceChange?: (stage: Stage, chance: number) => void;
  onStatusChange?: (offerId: string, newStatus: OfferStatus) => void;
  onDeleteRequested?: (offerId: string) => void;
  onEditRequested?: (offerId: string) => void;
  onInfoRequested?: (offerId: string) => void;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement",
  });
  const stageName = (stage: Stage) =>
    t(Stage[stage], { keyPrefix: "case.stages" });
  const p = (key: string) => t(`prospects.${key}`);

  // the line the offers hang on, drawn piece by piece in the rows it passes;
  // in the row that ends it, it stops behind what stands there
  const line = (ends = false) => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: ends ? "50%" : 0,
        left: "50%",
        width: 2,
        ml: "-1px",
        bgcolor: "divider",
      }}
    />
  );
  const onTheLine = (content: ReactNode, ends = false) => (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        py: 1,
      }}
    >
      {line(ends)}
      {/* the page's own colour under what stands on the line, so that the
          line does not show through a see-through fill */}
      <Box
        sx={{
          position: "relative",
          borderRadius: 999,
          bgcolor: "background.default",
        }}
      >
        {content}
      </Box>
    </Box>
  );

  const offerRow = (offer: Offer) => {
          const look = offerLook(theme, offer.status);
          const ArrowIcon = ARROW_ICONS[offer.status];
          const sent = ArrowIcon !== undefined;
          const { byParty } = offer;
          const recipient = byParty ? opponentName : partyName;
          return (
            <Fragment key={offer.id}>
              <Box sx={{ display: "grid", gridTemplateColumns: COLUMNS }}>
                <Box sx={{ gridColumn: 2, gridRow: 1, position: "relative" }}>
                  {line()}
                  {/* from the card to its number, and on to the other side once
                      the offer is made */}
                  <Box
                    component="svg"
                    viewBox={`0 0 ${SPINE_WIDTH} 16`}
                    aria-hidden
                    sx={{
                      position: "absolute",
                      top: MARK_TOP - 8,
                      left: 0,
                      width: SPINE_WIDTH,
                      height: 16,
                      transform: byParty ? undefined : "scaleX(-1)",
                    }}
                  >
                    <path
                      d={`M0 8H${SPINE_WIDTH / 2}`}
                      stroke={look.color}
                      strokeWidth={2.5}
                    />
                    {sent && (
                      <>
                        <path
                          d={`M${SPINE_WIDTH / 2} 8H${SPINE_WIDTH - 8}`}
                          stroke={look.color}
                          strokeWidth={2.5}
                        />
                        <path
                          d={`M${SPINE_WIDTH - 12} 2l9 6-9 6z`}
                          fill={look.color}
                        />
                      </>
                    )}
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      top: MARK_TOP,
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: MARK_SIZE,
                      height: MARK_SIZE,
                      borderRadius: "50%",
                      // the colour of a draft is see-through: a card's own
                      // colour under it keeps the line from showing through
                      bgcolor: "background.paper",
                      backgroundImage: `linear-gradient(${look.color}, ${look.color})`,
                      color: look.markInk,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      typography: "body2",
                      fontWeight: 700,
                    }}
                  >
                    {offer.number}
                  </Box>
                </Box>

                {/* where the offer went, and what became of it there */}
                <Box
                  sx={{
                    gridColumn: byParty ? 3 : 1,
                    gridRow: 1,
                    pt: `${MARK_TOP - ICON_SIZE / 2}px`,
                    px: 0.625,
                  }}
                >
                  {ArrowIcon && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: byParty ? "row" : "row-reverse",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: "none",
                          width: ICON_SIZE,
                          height: ICON_SIZE,
                          borderRadius: "50%",
                          bgcolor: look.color,
                          color: look.markInk,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ArrowIcon sx={{ fontSize: 14 }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        {t("timeline.to", { name: recipient })}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    gridColumn: byParty ? 1 : 3,
                    gridRow: 1,
                    py: 1.25,
                    display: "flex",
                    justifyContent: byParty ? "flex-end" : "flex-start",
                  }}
                >
                  <Box sx={{ width: "100%", maxWidth: CARD_MAX_WIDTH }}>
                    <SettlementOfferCard
                      offer={offer}
                      onDeleteRequested={
                        onDeleteRequested
                          ? () => onDeleteRequested(offer.id)
                          : undefined
                      }
                      onEditRequested={
                        onEditRequested
                          ? () => onEditRequested(offer.id)
                          : undefined
                      }
                      onInfoRequested={
                        onInfoRequested
                          ? () => onInfoRequested(offer.id)
                          : undefined
                      }
                      onStatusChange={
                        onStatusChange
                          ? (newStatus) => onStatusChange(offer.id, newStatus)
                          : undefined
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Fragment>
          );
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ minWidth: MIN_WIDTH }}>
        {/* what the lines of the stages say */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: STAGE_COLUMNS,
            columnGap: 1.75,
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: "divider",
            typography: "caption",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          <span>{p("stage")}</span>
          <Marked entry="chances">{p("chance")}</Marked>
          <Box component="span" sx={{ textAlign: "right" }}>
            {p("settlesAt")}
          </Box>
          <Box component="span" sx={{ textAlign: "right" }}>
            {p("settlesHere")}
          </Box>
          <span>{p("stillInDispute")}</span>
        </Box>

        {/* the two sides */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: COLUMNS,
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ textAlign: "right", fontWeight: 700 }}
          >
            {t("timeline.offersBy", { name: partyName })}
          </Typography>
          <Box sx={{ position: "relative", height: 34 }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                bottom: 0,
                left: "50%",
                width: 2,
                ml: "-1px",
                bgcolor: "divider",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "text.disabled",
                transform: "translate(-50%, -50%)",
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t("timeline.offersBy", { name: opponentName })}
          </Typography>
        </Box>

        {stages.map((line) => {
          const inStage = offers.filter(({ stage }) => stage === line.stage);
          const isCurrent = line.status === "current" && !settlement;
          const edge = `${EDGE}px solid ${
            isCurrent
              ? alpha(theme.palette.primary.main, CURRENT_EDGE_ALPHA)
              : theme.palette.divider
          }`;
          return (
            <Box
              key={line.stage}
              sx={{
                position: "relative",
                // over the line of the stage before, not under it
                mt: isCurrent ? `-${EDGE}px` : 0,
                borderTop: isCurrent ? edge : "none",
                borderBottom: edge,
                bgcolor: isCurrent
                  ? alpha(theme.palette.primary.main, CURRENT_ALPHA)
                  : line.status === "closed"
                    ? "action.hover"
                    : undefined,
              }}
            >
              <StageLine
                line={line}
                name={stageName(line.stage)}
                readOnly={!onChanceChange}
                outsideFor={partyName}
                onChange={(chance) => onChanceChange?.(line.stage, chance)}
              />
              {inStage.length > 0 && (
                <Box
                  sx={{
                    boxShadow: `inset 0 4px 5px -3px ${alpha(theme.palette.text.primary, SHADE_ALPHA)}`,
                  }}
                >
                  {inStage.map(offerRow)}
                </Box>
              )}
              {isCurrent && rangePanel}
            </Box>
          );
        })}

        {/* a settlement closes the timeline; otherwise the chances add up */}
        {settlement &&
          onTheLine(
            <Box
              sx={{
                display: "inline-flex",
                  alignItems: "center",
                  gap: 0.875,
                  pl: 1.375,
                  pr: 1.875,
                  py: 0.75,
                  borderRadius: 999,
                  border: `1px solid ${alpha(theme.palette.data.positive.main, 0.4)}`,
                  bgcolor: alpha(theme.palette.data.positive.main, 0.12),
                  color: theme.palette.data.positive.dark,
                  typography: "body2",
                  fontWeight: 700,
                }}
              >
                <AcceptedIcon sx={{ fontSize: 16 }} />
                {t("timeline.closed", {
                  stage: stageName(settlement.stage),
                  amount: currency.format(settlement.amount),
                })}
              </Box>,
              true,
            )}
        {resulting && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: STAGE_COLUMNS,
              columnGap: 1.75,
              alignItems: "center",
              minHeight: 44,
              px: 2,
              bgcolor: "action.hover",
              typography: "body2",
              ...TABULAR_NUMERALS,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {p("resulting")}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Box component="span" sx={{ color: "text.secondary" }}>
                {p("ofSettling")}
              </Box>
              <Box
                component="span"
                sx={{ width: 44, textAlign: "right", fontWeight: 700 }}
              >
                {percentage.format(resulting.settling)}
              </Box>
            </Box>
            <span />
            <span />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1.25,
              }}
            >
              <Box component="span" sx={{ color: "text.secondary" }}>
                {p("litigation")}
              </Box>
              <Box
                component="span"
                sx={{ width: 40, textAlign: "right", fontWeight: 700 }}
              >
                {percentage.format(resulting.litigation)}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SettlementTimeline;
